import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Document, Page } from 'react-pdf';

import {
  ArrowDownIcon,
  ArrowUpIcon,
  ArrowDownTrayIcon as DownloadIcon,
  MinusIcon,
  PlusIcon,
} from '@heroicons/react/20/solid';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { RenderDownloadProps } from '@react-pdf-viewer/get-file';
import { RenderGoToPageProps } from '@react-pdf-viewer/page-navigation';
import { thumbnailPlugin } from '@react-pdf-viewer/thumbnail';
import '@react-pdf-viewer/thumbnail/lib/styles/index.css';
import { toolbarPlugin } from '@react-pdf-viewer/toolbar';
import '@react-pdf-viewer/toolbar/lib/styles/index.css';
import {
  RenderCurrentScaleProps,
  RenderZoomInProps,
  RenderZoomOutProps,
} from '@react-pdf-viewer/zoom';
import { version as pdfjsVersion } from 'pdfjs-dist';
import styled from 'styled-components';

import { classNames } from '@/helpers/strings';
import { OrderDesignFile } from '@/types/IOrderV2';

import { containerStyles } from './PdfPreviewComponent.styles';

// Utility function for debouncing
const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Centralized error handling components
const PDFErrorComponent: React.FC<{
  error: Error | { message: string; name?: string };
  onRetry: () => void;
  context: 'document' | 'page' | 'viewer';
  isRetrying?: boolean;
}> = ({ error, onRetry, context, isRetrying = false }) => {
  // Determine if this is a network-related error
  const isNetworkError =
    error.message.toLowerCase().includes('network') ||
    error.message.toLowerCase().includes('fetch') ||
    error.message.toLowerCase().includes('cors') ||
    error.message.toLowerCase().includes('timeout');

  const getErrorMessage = () => {
    if (isNetworkError) {
      return 'Network connection issue. Please check your internet connection.';
    }
    if (context === 'page') {
      return 'Failed to load this page';
    }
    return 'Failed to load PDF document';
  };

  const getRetryButtonText = () => {
    if (isNetworkError) {
      return 'Retry Connection';
    }
    return 'Try Again';
  };

  return (
    <div
      style={{ padding: '20px', textAlign: 'center' }}
      role="alert"
      aria-live="polite"
    >
      <p style={{ marginBottom: '10px', color: '#dc2626' }}>
        {getErrorMessage()}
      </p>
      <p style={{ marginBottom: '15px', fontSize: '14px', color: '#6b7280' }}>
        {error.message}
      </p>
      <button
        onClick={onRetry}
        disabled={isRetrying}
        style={{
          padding: '8px 16px',
          backgroundColor: isRetrying ? '#9ca3af' : '#3b82f6',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: isRetrying ? 'not-allowed' : 'pointer',
          transition: 'background-color 0.2s',
          opacity: isRetrying ? 0.7 : 1,
        }}
        onMouseOver={e => {
          if (!isRetrying) {
            e.currentTarget.style.backgroundColor = '#2563eb';
          }
        }}
        onMouseOut={e => {
          if (!isRetrying) {
            e.currentTarget.style.backgroundColor = '#3b82f6';
          }
        }}
        aria-label={`${getRetryButtonText()} - ${
          context === 'page' ? 'page' : 'PDF document'
        }`}
      >
        {isRetrying ? 'Retrying...' : getRetryButtonText()}
      </button>
    </div>
  );
};

const PDFLoadingComponent: React.FC<{
  progress?: number;
  context: 'document' | 'page';
}> = ({ progress, context }) => (
  <div
    style={{ padding: '20px', textAlign: 'center' }}
    role="status"
    aria-live="polite"
  >
    <div style={{ marginBottom: '10px' }}>
      Loading {context === 'page' ? 'page' : 'PDF'}...
    </div>
    {progress !== undefined && (
      <div style={{ fontSize: '14px', color: '#6b7280' }}>
        {Math.round(progress)}%
      </div>
    )}
  </div>
);

// Create styled component outside of the main component
const PDFContainer = styled.div(containerStyles);

interface IPdfPreviewComponent {
  setError: React.Dispatch<React.SetStateAction<boolean>>;
  designFile?: OrderDesignFile;
  setSuccess: React.Dispatch<React.SetStateAction<string>>;
}
export type Ref = {
  downLoadDesign(): void;
};

export const PdfPreView: React.FC<IPdfPreviewComponent> = ({
  setError,
  setSuccess,
  designFile,
}) => {
  type ScreenSize = 'mobile' | 'desktop';

  // Initialize screen size based on window width
  const [screenSize, setScreenSize] = useState<ScreenSize>(() =>
    window.innerWidth <= 425 ? 'mobile' : 'desktop',
  );
  const [numPages, setNumPages] = useState<number>(0);
  const [retryKey, setRetryKey] = useState<number>(0); // Key to force component re-render
  const [isRetrying, setIsRetrying] = useState<boolean>(false); // Track retry state

  // Helper variables to make JSX conditions more readable and maintainable
  const isMobile = screenSize === 'mobile';
  const isDesktop = screenSize === 'desktop';

  // Memoize file URL to prevent unnecessary re-fetching for both viewers
  const memoizedFileUrl = useMemo(() => {
    return designFile?.original_url || null;
  }, [designFile?.original_url]);

  // Derive file object for react-pdf from the memoized URL
  const memoizedFile = useMemo(() => {
    if (!memoizedFileUrl) return null;
    return { url: memoizedFileUrl };
  }, [memoizedFileUrl]);

  // Memoize plugin instances to prevent recreation on each render
  const toolbarPluginInstance = toolbarPlugin();
  const thumbnailPluginInstance = thumbnailPlugin();

  // Destructure plugin components after memoization
  const { Thumbnails } = thumbnailPluginInstance;
  const { Toolbar } = toolbarPluginInstance;

  // Debounced resize handler to prevent excessive re-renders
  const debouncedHandleResize = useCallback(
    debounce(() => {
      setScreenSize(window.innerWidth <= 425 ? 'mobile' : 'desktop');
    }, 150),
    [],
  );

  // Handle window resize
  useEffect(() => {
    // Set initial screen size
    setScreenSize(window.innerWidth <= 425 ? 'mobile' : 'desktop');

    window.addEventListener('resize', debouncedHandleResize);

    return () => {
      window.removeEventListener('resize', debouncedHandleResize);
    };
  }, [debouncedHandleResize]);

  // Enhanced error handling functions
  const handleLoadError = useCallback(
    (error: Error) => {
      console.error('PDF load error:', error);
      setError(true);
    },
    [setError],
  );

  const handleSourceError = useCallback(
    (error: Error) => {
      console.error('PDF source error:', error);
      setError(true);
    },
    [setError],
  );

  const handleLoadProgress = useCallback(
    ({ loaded, total }: { loaded: number; total: number }) => {
      // Optional: Add progress indicator
      console.log(`Loading progress: ${Math.round((loaded / total) * 100)}%`);
    },
    [],
  );

  const handleRetry = useCallback(() => {
    // Reset error state and force component re-render by updating retry key
    setError(false);
    setIsRetrying(true);
    setRetryKey(prev => prev + 1);
    setNumPages(0);

    // Reset retry state after a short delay to allow for re-rendering
    setTimeout(() => {
      setIsRetrying(false);
    }, 1000);
  }, [setError]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  // Don't render if no file URL
  if (!memoizedFileUrl) {
    return (
      <PDFContainer>
        <div
          style={{
            padding: '40px',
            textAlign: 'center',
            color: '#6b7280',
          }}
          role="alert"
          aria-live="polite"
        >
          <p>No PDF file specified.</p>
          <p style={{ fontSize: '14px', marginTop: '8px' }}>
            Please select a design file to preview.
          </p>
        </div>
      </PDFContainer>
    );
  }

  return (
    <PDFContainer>
      <Worker
        workerUrl={`https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsVersion}/pdf.worker.min.js`}
      />
      {memoizedFileUrl && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
          className="h-full"
        >
          {!isMobile && (
            <div className="mb-5 flex">
              <div className="h-full lg:w-64 "> </div>
              <Toolbar>
                {({
                  CurrentPageLabel,
                  CurrentScale,
                  GoToNextPage,
                  GoToPreviousPage,
                  ZoomIn,
                  ZoomOut,
                  Download,
                }: any) => {
                  return (
                    <div className="flex justify-between align-middle w-full flex-wrap gap-3">
                      <div className="flex justify-between align-middle ">
                        <div className="pdf-paginate flex justify-between items-center border lg:h-full rounded-md">
                          <GoToNextPage>
                            {({ isDisabled, onClick }: RenderGoToPageProps) => (
                              <button
                                style={{
                                  cursor: isDisabled
                                    ? 'not-allowed'
                                    : 'pointer',
                                }}
                                disabled={isDisabled}
                                onClick={onClick}
                                className="hover:text-brand-400 lg:pl-7 lg:pr-2 pl-4 pr-2 py-2 lg:h-full text-gray-700 focus:outline-none outline-none"
                              >
                                <ArrowDownIcon className="stroke-1.3 z-20 lg:h-5 lg:w-5 w-3 h-3" />
                              </button>
                            )}
                          </GoToNextPage>

                          <CurrentPageLabel>
                            {(props: any) => (
                              <span className="text-gray-700 text-xs font-normal">{`${
                                Number(props.currentPage) + 1
                              } / ${props.numberOfPages}`}</span>
                            )}
                          </CurrentPageLabel>

                          <GoToPreviousPage>
                            {({ isDisabled, onClick }: RenderGoToPageProps) => (
                              <button
                                style={{
                                  cursor: isDisabled
                                    ? 'not-allowed'
                                    : 'pointer',
                                }}
                                className="hover:text-brand-400 lg:pl-2 lg:pr-7 pr-4 pl-2 py-1 lg:h-full text-gray-700 focus:outline-none outline-none"
                                disabled={isDisabled}
                                onClick={onClick}
                              >
                                <ArrowUpIcon className="stroke-1.3 z-20 lg:h-5 lg:w-5 w-3 h-3" />
                              </button>
                            )}
                          </GoToPreviousPage>
                        </div>

                        <div className="pdf-paginate flex justify-between items-center border lg:h-full rounded-md ml-3">
                          <ZoomOut>
                            {({ onClick }: RenderZoomOutProps) => (
                              <button
                                onClick={onClick}
                                className="hover:text-brand-400 lg:pl-7 lg:pr-2 pl-4 pr-2 py-2 lg:h-full text-gray-700 focus:outline-none outline-none"
                              >
                                <MinusIcon className="stroke-1.3 z-20 lg:h-5 lg:w-5 h-3 w-3" />
                              </button>
                            )}
                          </ZoomOut>

                          <CurrentScale>
                            {(props: RenderCurrentScaleProps) => (
                              <span className="text-gray-700 text-xs font-normal">{`${Math.round(
                                props.scale * 100,
                              )}%`}</span>
                            )}
                          </CurrentScale>

                          <ZoomIn>
                            {({ onClick }: RenderZoomInProps) => (
                              <button
                                onClick={onClick}
                                className="hover:text-brand-400 pl-2 py-2 lg:pr-7 pr-4 h-full text-gray-700 focus:outline-none outline-none"
                              >
                                <PlusIcon className="stroke-1.3 z-20 lg:h-5 lg:w-5 h-3 w-3" />
                              </button>
                            )}
                          </ZoomIn>
                        </div>
                      </div>
                      <div className="py-0 px-1 ml-auto flex-right">
                        <Download>
                          {({ onClick }: RenderDownloadProps) => (
                            <button
                              className="bg-brand-500 rounded-full p-2 flex justify-center item-center lg:rounded-md text-white focus:outline-none outline-none"
                              onClick={() => {
                                try {
                                  const click = onClick();
                                  setSuccess('File downloaded successfully!');
                                  return click;
                                } catch (error) {
                                  setError(true);
                                  return () => {};
                                }
                              }}
                            >
                              <DownloadIcon className="h-5 w-5 text-white" />
                              <p className="lg:ml-2 text-sm font-medium ">
                                {isDesktop ? 'Download PDF' : ''}
                              </p>
                            </button>
                          )}
                        </Download>
                      </div>
                    </div>
                  );
                }}
              </Toolbar>
            </div>
          )}
          <div
            style={{
              flex: 1,
            }}
            className="overflow-hidden"
          >
            <div
              className={classNames(
                'rpv-core__viewer flex h-full overflow-y-scroll overflow-x-hidden',
                isMobile ? 'bg-gray-300' : '',
              )}
            >
              <div className="overflow-auto lg:block hidden">
                <Thumbnails />
              </div>
              {isMobile ? (
                <Document
                  key={`mobile-pdf-${retryKey}`} // Force re-render on retry
                  file={memoizedFile}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={handleLoadError}
                  onSourceError={handleSourceError}
                  onLoadProgress={handleLoadProgress}
                  loading={<PDFLoadingComponent context="document" />}
                  error={
                    <PDFErrorComponent
                      error={new Error('Unable to load PDF document')}
                      onRetry={handleRetry}
                      context="document"
                      isRetrying={isRetrying}
                    />
                  }
                  className=" h-full bg-gray-300"
                >
                  {Array.from(new Array(numPages), (_, index) => (
                    <Page
                      key={`page_${index + 1}`}
                      pageNumber={index + 1}
                      className="mx-auto first:pt-1"
                      loading={<PDFLoadingComponent context="page" />}
                      error={
                        <PDFErrorComponent
                          error={new Error('Unable to load this page')}
                          onRetry={handleRetry}
                          context="page"
                          isRetrying={isRetrying}
                        />
                      }
                    />
                  ))}
                </Document>
              ) : (
                <Viewer
                  key={`desktop-pdf-${retryKey}`} // Force re-render on retry
                  fileUrl={memoizedFileUrl}
                  plugins={[toolbarPluginInstance, thumbnailPluginInstance]}
                  onDocumentLoad={e => {
                    setNumPages(e.doc.numPages);
                  }}
                  renderError={error => (
                    <PDFErrorComponent
                      error={{
                        message: error.message || 'Unknown error occurred',
                        name: error.name || 'PDFError',
                      }}
                      onRetry={handleRetry}
                      context="viewer"
                      isRetrying={isRetrying}
                    />
                  )}
                  renderLoader={percentages => (
                    <PDFLoadingComponent
                      progress={percentages}
                      context="document"
                    />
                  )}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </PDFContainer>
  );
};
export default React.memo(PdfPreView);
